import { zodResolver } from "@hookform/resolvers/zod";
import { X } from "lucide-react";
import { useEffect, useState, useSyncExternalStore } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { BrandButton } from "@/components/brand/brand-button";
import { Form, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { cn } from "@/lib/utils";
import { emailSchema } from "@/lib/validation";

const DISMISSED_KEY = "agentmrr_digest_dismissed";
const SUBSCRIBED_KEY = "agentmrr_digest_subscribed";
const SCROLL_THRESHOLD_PX = 400;

function readLocalStorage(key: string): boolean {
	if (typeof window === "undefined") return false;
	return localStorage.getItem(key) === "1";
}

const useIsClient = () =>
	useSyncExternalStore(
		() => () => {},
		() => true,
		() => false,
	);

const digestSchema = z.object({ email: emailSchema });
type DigestForm = z.infer<typeof digestSchema>;

export function DigestWidget() {
	const isClient = useIsClient();
	const [hidden, setHidden] = useState(
		() => readLocalStorage(DISMISSED_KEY) || readLocalStorage(SUBSCRIBED_KEY),
	);
	const [success, setSuccess] = useState(false);
	const [scrolledPastThreshold, setScrolledPastThreshold] = useState(false);

	useEffect(() => {
		if (typeof window === "undefined") return;
		if (window.scrollY > SCROLL_THRESHOLD_PX) {
			setScrolledPastThreshold(true);
			return;
		}
		const onScroll = () => {
			if (window.scrollY > SCROLL_THRESHOLD_PX) {
				setScrolledPastThreshold(true);
				window.removeEventListener("scroll", onScroll);
			}
		};
		window.addEventListener("scroll", onScroll, { passive: true });
		return () => window.removeEventListener("scroll", onScroll);
	}, []);

	const form = useForm<DigestForm>({
		resolver: zodResolver(digestSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: { email: "" },
	});

	if (!isClient || hidden || !scrolledPastThreshold) return null;

	const dismiss = () => {
		localStorage.setItem(DISMISSED_KEY, "1");
		setHidden(true);
	};

	const onSubmit = async (values: DigestForm) => {
		try {
			const res = await fetch("/api/newsletter/subscribe", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ email: values.email, source: "homepage" }),
			});

			if (res.ok || res.status === 409) {
				localStorage.setItem(SUBSCRIBED_KEY, "1");
				setSuccess(true);
				setTimeout(() => setHidden(true), 2200);
				return;
			}
			form.setError("root.serverError", {
				message: "Something went wrong, try again",
			});
		} catch {
			form.setError("root.serverError", {
				message: "Something went wrong, try again",
			});
		}
	};

	const loading = form.formState.isSubmitting;
	const serverError = form.formState.errors.root?.serverError?.message;
	const emailError = form.formState.errors.email?.message;

	return (
		<div className="fixed z-50 hidden w-[320px] animate-in fade-in slide-in-from-bottom-4 duration-500 [animation-delay:1500ms] [animation-fill-mode:both] lg:block lg:bottom-6 lg:right-4">
			<div className="border border-border bg-popover">
				<div className="flex items-center justify-between border-b border-border px-4 py-2">
					<span className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
						&gt; Radar
					</span>
					<button
						type="button"
						onClick={dismiss}
						className="-mr-2 inline-flex size-10 items-center justify-center text-muted-foreground transition-[color,transform] hover:text-brand active:scale-[0.96]"
						aria-label="Dismiss"
					>
						<X className="size-3" />
					</button>
				</div>

				<div className="p-4">
					{success ? (
						<div className="animate-in fade-in zoom-in-95 py-2 text-center duration-300">
							<p className="font-mono font-normal text-base uppercase tracking-[0.08em] text-brand">
								Subscribed
							</p>
							<p className="mt-1 font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground">
								You're on the list
							</p>
						</div>
					) : (
						<>
							<div className="mb-3">
								<p className="font-mono font-normal text-base leading-tight tracking-[-0.01em] text-foreground">
									Who's climbing?
								</p>
								<p className="mt-1 text-pretty text-sm leading-normal text-muted-foreground">
									Track top movers, new launches, and milestone hits.
								</p>
							</div>

							<Form {...form}>
								<form
									onSubmit={form.handleSubmit(onSubmit)}
									className="space-y-2"
									noValidate
								>
									<FormField
										control={form.control}
										name="email"
										render={({ field }) => (
											<FormItem>
												<div
													className={cn(
														"flex items-center gap-2 border border-border bg-background px-3 py-2 transition-colors focus-within:border-brand",
														emailError &&
															"border-destructive focus-within:border-destructive",
													)}
												>
													<svg
														aria-hidden="true"
														className="size-3 shrink-0 text-muted-foreground"
														viewBox="0 0 16 16"
														fill="currentColor"
													>
														<path d="M2 4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v.5l-6 3.75L2 4.5V4Zm0 1.75V12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V5.75L8 9.5 2 5.75Z" />
													</svg>
													<input
														type="email"
														placeholder="enter@your.email"
														className="min-w-0 flex-1 bg-transparent font-mono text-sm text-foreground placeholder:text-muted-foreground/70 outline-none"
														aria-invalid={!!emailError}
														{...field}
													/>
												</div>
												<FormMessage className="font-mono text-sm uppercase tracking-[0.14em]" />
											</FormItem>
										)}
									/>

									<BrandButton
										type="submit"
										disabled={loading}
										className="group w-full"
									>
										{loading ? (
											<span className="animate-pulse">Subscribing…</span>
										) : (
											<>
												Get the Radar
												<span className="transition-transform duration-150 group-hover:translate-x-0.5">
													→
												</span>
											</>
										)}
									</BrandButton>

									{serverError && (
										<p className="text-center font-mono text-sm uppercase tracking-[0.14em] text-destructive">
											{serverError}
										</p>
									)}
								</form>
							</Form>
						</>
					)}
				</div>
			</div>
		</div>
	);
}
